import {
  FlexibleBlockBrickButton,
  FlexibleBlockBrickImage,
  FlexibleBlockBrickText,
  FlexibleBlockBrickTimeline,
  FlexibleBlockBrickTimelineStyle,
  FlexibleBlockProps,
  FlexibleBlockTemplate,
  FlexibleBlockTemplateTimeline,
  FlexibleBlockBrick,
  DynamicLinkType,
  PortalTextType,
} from '@nx-frontend/shared/types';
import { getWrapperProps } from '@nx-frontend/shared/utils/blocks';
import { FlexibleBlockView, brickCommon } from './common';

export const getTimelineBlockTemplate = (): FlexibleBlockProps<FlexibleBlockTemplateTimeline>[] => {
  const brickImage: FlexibleBlockBrickImage = {
    ...brickCommon,
    brickType: 'image',
    brickName: 'Image',
    switchable: true,
    src: '/assets/img/blocks/timeline/timeline-img.png',
  };

  const brickDescription: FlexibleBlockBrickText = {
    ...brickCommon,
    brickType: 'text',
    brickName: 'Description',
    switchable: true,
    placeholder: 'Here, you can add some information about widget',
    text: 'The timeline is the backbone of our project that will lead us to success! It is a structured plan that outlines key phases, milestones and activities.',
    theme: {
      text: {
        main: PortalTextType.SURFACE,
      },
    },
  };

  const brickButton: FlexibleBlockBrickButton = {
    ...brickCommon,
    brickType: 'button',
    brickName: 'Button',
    switchable: true,
    text: 'View project',
    dynamicLink: { type: DynamicLinkType.EXTERNAL, url: 'https://en.wikipedia.org/wiki/Project' },
  };

  const brickTimeline: FlexibleBlockBrickTimeline = {
    ...brickCommon,
    brickType: 'timeline',
    brickName: 'Step group',
    style: FlexibleBlockBrickTimelineStyle.HORIZONTAL,
    items: [
      {
        done: true,
        title: 'Vision',
        dynamicLink: { type: DynamicLinkType.EXTERNAL, url: 'https://en.wikipedia.org/wiki/Goal' },
      },
      {
        done: true,
        title: 'Arrangements',
        dynamicLink: { type: DynamicLinkType.EXTERNAL, url: 'https://en.wikipedia.org/wiki/Invoice' },
      },
      {
        done: false,
        title: 'Launch',
        dynamicLink: { type: DynamicLinkType.EXTERNAL, url: 'https://en.wikipedia.org/wiki/New_product_development' },
      },
    ],
  };

  const block: FlexibleBlockProps<FlexibleBlockTemplateTimeline> = {
    title: 'Timeline',
    wrapper: getWrapperProps('blank'),
    template: {
      type: 'timeline',
      view: 'horizontal',
    },
    bricks: [
      { ...brickImage, index: 0 },
      { ...brickDescription, index: 1 },
      { ...brickTimeline, index: 2, style: FlexibleBlockBrickTimelineStyle.HORIZONTAL },
      { ...brickButton, index: 3 },
    ],
    className: [],
  };

  return [
    convertTimelineBlockView(block, {
      type: 'timeline',
      view: 'horizontal',
    }),
    convertTimelineBlockView(block, {
      type: 'timeline',
      view: 'vertical',
    }),
  ];
};

export const getTimelineBlockViews = (): FlexibleBlockView<FlexibleBlockTemplateTimeline>[] => {
  return [
    {
      key: 'horizontal',
      template: { type: 'timeline', view: 'horizontal' },
      title: 'Horizontal timeline with image',
      image: '/assets/img/blocks/timeline/view-horizontal.png',
      preview: '/assets/img/blocks/timeline/preview-horizontal.png',
    },
    {
      key: 'vertical',
      template: { type: 'timeline', view: 'vertical' },
      title: 'Vertical timeline with image',
      image: '/assets/img/blocks/timeline/view-vertical.png',
      preview: '/assets/img/blocks/timeline/preview-vertical.png',
    },
  ];
};

export const convertTimelineBlockView = <T extends FlexibleBlockTemplate = FlexibleBlockTemplateTimeline>(
  block: FlexibleBlockProps<T>,
  template: T
): FlexibleBlockProps<T> => {
  const newBlock = { ...block, template };

  newBlock.bricks = newBlock.bricks.reduce<FlexibleBlockBrick[]>((acc, brick) => {
    if (brick.brickType === 'timeline') {
      acc.push({
        ...brick,
        style: FlexibleBlockBrickTimelineStyle[template.view === 'vertical' ? 'VERTICAL' : 'HORIZONTAL'],
      });
    } else {
      acc.push(brick);
    }
    return acc;
  }, []);

  return newBlock;
};
